/**
 * Fields of the pizza catalogue that more than one module reads - `FRONTEND.md` §9.671.
 *
 * **`products.pizza_ingredient_ids` is read by the product dialog and by the kitchen**, and two readers of one
 * column in two modules is how they come to disagree about what a pizza is made of - the shape this repository
 * keeps paying for (`agos-one-reader-spelled-twice`). So the reader lives here, and `manager-menu` re-exports it
 * from the path its dialog always imported.
 */

/**
 * `products.pizza_ingredient_ids` as a list - §9.190, moved here by §9.671.
 *
 * Stored as a JSON array of ids, which is what legacy writes. Anything else reads as *none chosen* rather than
 * throwing: a product row written by hand or by an older client must not break the editor that has to fix it, nor
 * the kitchen card that has to draw it.
 */
export function parseIngredientIds(raw: unknown): number[] {
  if (Array.isArray(raw)) return raw.filter((id): id is number => typeof id === 'number')
  if (typeof raw !== 'string' || raw.trim() === '') return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : []
  } catch {
    return []
  }
}

/**
 * Whether a pizza ingredient goes on after the pizza leaves the oven - `FRONTEND.md` §9.673, `BACKEND.md` §10A.339.
 *
 * **Read off the untyped row** until the generated client carries `afterBaking`: `GET /pizza-ingredients` answers
 * it from `V72` on, and the editor and the kitchen both ask through this one reader. Only an explicit `true` counts,
 * so a server older than `V72` - which sends nothing - reads as *baked with the pizza*, which is what every
 * ingredient was before the column existed.
 */
export function isAfterBaking(row: unknown): boolean {
  return typeof row === 'object' && row !== null && (row as { afterBaking?: unknown }).afterBaking === true
}
